import React, { useEffect, useRef } from "react";
import { Bold, Italic, Underline, Heading2, Heading3, List, Highlighter, Palette, Link as LinkIcon, AlignLeft, AlignCenter, AlignRight, RemoveFormatting } from "lucide-react";

/**
 * Lightweight contentEditable rich text editor.
 * - Bold / Italic / Underline / H2 / H3 / Lists / Color / Highlight / Link / Align
 * - Emits HTML via onChange(html)
 * - Uses document.execCommand (deprecated but reliable for simple needs)
 */
export default function RichTextEditor({ value, onChange, placeholder = "Scrie descrierea aici..." }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, [value]);

  const exec = (cmd, val = null) => {
    ref.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(ref.current.innerHTML);
  };

  const onInput = () => onChange(ref.current.innerHTML);

  const setLink = () => {
    const url = window.prompt("URL link (https://...)");
    if (url) exec("createLink", url);
  };

  const COLORS = ["#111827", "#1ebe57", "#dc2626", "#2563eb", "#d97706", "#7c3aed", "#0891b2", "#6b7280"];
  const BG = ["transparent", "#fef3c7", "#dbeafe", "#dcfce7", "#fee2e2", "#f3e8ff", "#fce7f3", "#e5e7eb"];

  return (
    <div className="border border-gray-300 bg-white" data-testid="rich-editor">
      <div className="flex flex-wrap items-center gap-1 px-2 py-2 border-b border-gray-200 bg-gray-50">
        <ToolBtn onClick={() => exec("bold")} title="Bold (Ctrl+B)"><Bold size={14} /></ToolBtn>
        <ToolBtn onClick={() => exec("italic")} title="Italic"><Italic size={14} /></ToolBtn>
        <ToolBtn onClick={() => exec("underline")} title="Subliniat"><Underline size={14} /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec("formatBlock", "h2")} title="Titlu mare"><Heading2 size={14} /></ToolBtn>
        <ToolBtn onClick={() => exec("formatBlock", "h3")} title="Subtitlu"><Heading3 size={14} /></ToolBtn>
        <ToolBtn onClick={() => exec("formatBlock", "p")} title="Paragraf">P</ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec("insertUnorderedList")} title="Listă"><List size={14} /></ToolBtn>
        <Sep />
        <ToolBtn onClick={() => exec("justifyLeft")} title="Stânga"><AlignLeft size={14} /></ToolBtn>
        <ToolBtn onClick={() => exec("justifyCenter")} title="Centru"><AlignCenter size={14} /></ToolBtn>
        <ToolBtn onClick={() => exec("justifyRight")} title="Dreapta"><AlignRight size={14} /></ToolBtn>
        <Sep />
        <ColorPicker icon={<Palette size={14} />} colors={COLORS} title="Culoare text" onPick={(c) => exec("foreColor", c)} />
        <ColorPicker icon={<Highlighter size={14} />} colors={BG} title="Fundal text" onPick={(c) => exec("hiliteColor", c)} />
        <Sep />
        <ToolBtn onClick={setLink} title="Link"><LinkIcon size={14} /></ToolBtn>
        <ToolBtn onClick={() => exec("removeFormat")} title="Șterge formatare"><RemoveFormatting size={14} /></ToolBtn>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        data-placeholder={placeholder}
        className="prose-anaiza min-h-[180px] max-h-[420px] overflow-y-auto px-4 py-3 outline-none focus:bg-white text-sm leading-relaxed empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400"
        style={{ wordBreak: "break-word" }}
        data-testid="rich-editor-content"
      />
    </div>
  );
}

function ToolBtn({ children, onClick, title }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      title={title}
      className="px-2 py-1.5 hover:bg-gray-200 text-gray-700 text-xs font-bold inline-flex items-center justify-center min-w-[28px]"
    >
      {children}
    </button>
  );
}
function Sep() {
  return <span className="w-px h-5 bg-gray-300 mx-1" />;
}
function ColorPicker({ icon, colors, title, onPick }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <ToolBtn onClick={() => setOpen((o) => !o)} title={title}>{icon}</ToolBtn>
      {open && (
        <div className="absolute z-10 top-full mt-1 left-0 bg-white border border-gray-300 p-2 grid grid-cols-4 gap-1 shadow-lg">
          {colors.map((c) => (
            <button
              key={c}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(c); setOpen(false); }}
              className="w-6 h-6 border border-gray-300 hover:scale-110 transition"
              style={{ background: c === "transparent" ? "repeating-conic-gradient(#eee 0% 25%, #fff 0% 50%) 50% / 8px 8px" : c }}
              title={c}
            />
          ))}
        </div>
      )}
    </div>
  );
}
